"use client";

import * as React from "react";
import Confetti from "react-confetti";

import { cn } from "@/lib/utils";

/** Brand confetti palette from Figma 6744:31653 / 6731:31063. */
const CONFETTI_COLORS = [
  "#001EFF",
  "#5486FA",
  "#FE634D",
  "#EB5029",
  "#CC61CC",
  "#DB41BD",
  "#E4EDFF",
  "#FFD6F0",
] as const;

const CONFETTI_CANVAS_CLASS =
  "pointer-events-none absolute inset-0 h-full w-full";

/** Near react-confetti defaults — https://alampros.github.io/react-confetti/?args=gravity:0.118;wind:0.027 */
const CONFETTI_PHYSICS = {
  gravity: 0.118,
  wind: 0.027,
  friction: 0.99,
  numberOfPieces: 100,
  // Default is 5000ms; shortened to fit the 2.4s fade-out window.
  tweenDuration: 2000,
  initialVelocityY: { min: -10, max: 0 },
} as const;

interface ConfettiSpill {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface ConfettiCornerSource {
  source: { x: number; y: number; w: number; h: number };
  initialVelocityX: { min: number; max: number };
  initialVelocityY?: { min: number; max: number };
}

/** Extra canvas padding so particles can spill past the content box (Figma frames). */
function getConfettiSpill(contentWidth: number): ConfettiSpill {
  if (contentWidth >= 1024) {
    return { top: 48, right: 230, bottom: 160, left: 230 };
  }

  if (contentWidth >= 768) {
    return {
      top: 48,
      right: Math.round(contentWidth * 0.28),
      bottom: 144,
      left: Math.round(contentWidth * 0.28),
    };
  }

  return {
    top: 40,
    right: Math.round(contentWidth * 0.42),
    bottom: 128,
    left: Math.round(contentWidth * 0.42),
  };
}

function getConfettiCornerSources(
  contentWidth: number,
  contentHeight: number,
  spill: ConfettiSpill
): { bottomLeft: ConfettiCornerSource; topRight: ConfettiCornerSource } {
  const sourceSize = contentWidth < 768 ? 16 : 20;

  return {
    bottomLeft: {
      source: {
        x: spill.left,
        y: spill.top + contentHeight - sourceSize,
        w: sourceSize,
        h: sourceSize,
      },
      initialVelocityX: { min: 4, max: 16 },
      initialVelocityY: { min: -22, max: -10 },
    },
    topRight: {
      source: {
        x: spill.left + contentWidth - sourceSize,
        y: spill.top,
        w: sourceSize,
        h: sourceSize,
      },
      initialVelocityX: { min: -8, max: 0 },
      initialVelocityY: { min: -10, max: 4 },
    },
  };
}

interface FundingEligibilityConfettiProps {
  className?: string;
}

/** Figma 6731:31046 desktop / 6744:31572 mobile — dual corner confetti bursts. */
export function FundingEligibilityConfetti({
  className,
}: FundingEligibilityConfettiProps) {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [contentSize, setContentSize] = React.useState({ width: 0, height: 0 });
  const [reduceMotion, setReduceMotion] = React.useState(false);
  const [isReady, setIsReady] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateReduceMotion = () => setReduceMotion(mediaQuery.matches);

    updateReduceMotion();
    mediaQuery.addEventListener("change", updateReduceMotion);
    return () => mediaQuery.removeEventListener("change", updateReduceMotion);
  }, []);

  React.useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }

    const updateSize = () => {
      const { width, height } = content.getBoundingClientRect();
      setContentSize({ width, height });
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setIsReady(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, []);

  if (reduceMotion) {
    return null;
  }

  const { width: contentWidth, height: contentHeight } = contentSize;
  const shouldRender = isReady && contentWidth > 0 && contentHeight > 0;
  const spill = getConfettiSpill(contentWidth);
  const canvasWidth = contentWidth + spill.left + spill.right;
  const canvasHeight = contentHeight + spill.top + spill.bottom;
  const corners = getConfettiCornerSources(contentWidth, contentHeight, spill);

  const sharedProps = {
    width: canvasWidth,
    height: canvasHeight,
    numberOfPieces: CONFETTI_PHYSICS.numberOfPieces,
    recycle: false,
    gravity: CONFETTI_PHYSICS.gravity,
    friction: CONFETTI_PHYSICS.friction,
    wind: CONFETTI_PHYSICS.wind,
    colors: [...CONFETTI_COLORS] as string[],
    tweenDuration: CONFETTI_PHYSICS.tweenDuration,
    className: CONFETTI_CANVAS_CLASS,
  };

  return (
    <div
      ref={contentRef}
      aria-hidden
      data-testid="funding-eligibility-confetti"
      className={cn(
        "pointer-events-none absolute inset-0 z-50 overflow-visible",
        className
      )}
    >
      {shouldRender ? (
        <div
          className="funding-eligibility-confetti-burst absolute overflow-visible"
          style={{
            left: -spill.left,
            top: -spill.top,
            width: canvasWidth,
            height: canvasHeight,
          }}
        >
          <Confetti
            {...sharedProps}
            confettiSource={corners.bottomLeft.source}
            initialVelocityX={corners.bottomLeft.initialVelocityX}
            initialVelocityY={corners.bottomLeft.initialVelocityY}
          />
          <Confetti
            {...sharedProps}
            confettiSource={corners.topRight.source}
            initialVelocityX={corners.topRight.initialVelocityX}
            initialVelocityY={corners.topRight.initialVelocityY}
          />
        </div>
      ) : null}
    </div>
  );
}
